// src/components/alert/Preflight.tsx

import { CheckTwoTone, CloseTwoTone, MemoryTwoTone } from '@mui/icons-material'
import {
  Alert,
  AlertTitle,
  Avatar,
  Chip,
  Collapse,
  LinearProgress,
  ListItem,
  Stack,
  useTheme,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { SensorId, SensorName, SensorPool } from '@/types/domain/sensor'

import { useSensorPool } from '../../hooks'

type PreflightProps = {
  reportDate: Date
  sensors: SensorId[]
  factors: any[]
  onChange: (problematic: SensorId[]) => void
}

export const Preflight = ({
  reportDate,
  sensors,
  factors,
  onChange,
}: PreflightProps) => {
  const sPool: SensorPool = useSensorPool()

  const theme = useTheme()
  const success = theme.palette.success.main
  const error = theme.palette.error.main

  const [showPreflight, setShowPreflight] = useState<boolean>(false)
  const [problematic, setProblematic] = useState<SensorId[]>([])

  // if reportDate changes, reset problematic sensorIds
  useEffect(() => setProblematic([]), [reportDate, sensors, factors])

  // factors are not allowed to overlap dates
  // expected behavior: 1 factor is remaining after applying filters,
  // because only 1 factor must be left
  // if factor length is other than 1, add current id to problematic sensorIds

  useEffect(() => {
    const calculateProblematic = (sensors, factors, reportDate) => {
      const FACTOR_WITHIN_DATE = factor => {
        const { period_end, period_start } = factor.attributes
        const fromDate = new Date(period_start)
        const toDate = new Date(period_end)
        const reportYear = reportDate.getFullYear()
        const reportMonth = reportDate.getMonth()
        const lastDay = new Date(
          reportYear,
          reportMonth,
          new Date(reportYear, reportMonth, 0).getDate()
        )

        return reportDate >= fromDate && lastDay <= toDate
      }

      sensors.forEach((id: SensorId) => {
        const SENSORID = f => f.attributes.sensor_id === id

        const fRecord = factors.filter(SENSORID)?.filter(FACTOR_WITHIN_DATE)

        setProblematic(
          fRecord?.length !== 1
            ? prev => (prev.includes(id) ? prev : [...prev, id])
            : prev => prev
        )
      })
    }

    if (sensors.length === 0 || factors.length === 0 || !reportDate) return

    calculateProblematic(sensors, factors, reportDate)
  }, [sensors, factors, reportDate])

  useEffect(() => {
    onChange(problematic)
  }, [onChange, problematic])

  if (sensors.length === 0 || factors.length === 0 || !reportDate)
    return <LinearProgress />

  const onTogglePreflight = () => setShowPreflight(prev => !prev)

  return (
    <Alert
      severity={problematic.length === 0 ? 'success' : 'error'}
      sx={{ mx: 5 }}
    >
      <AlertTitle onClick={onTogglePreflight} sx={{ cursor: 'pointer' }}>
        Preflight:{' '}
        {problematic.length === 0
          ? 'Für alle ausgewählten Verbrauchssensoren ist die Erstellung des Reports möglich.'
          : 'Für folgende, ausgewählte Verbrauchssensoren ist die Generierung des Reports nicht möglich.'}
        {showPreflight ? '▲' : '▼'}
      </AlertTitle>

      <Collapse in={showPreflight}>
        {sensors.map((id: SensorId) => {
          const sRecord = sPool.find(f => Number(f.id) === id)
          const name: SensorName = sRecord?.attributes?.name
          const factor = factors?.find(f => f.attributes.sensor_id === id)
          const period_start: Date = new Date(factor?.attributes.period_start)
          const period_end: Date = new Date(factor?.attributes.period_end)
          const label: string = `${period_start.toLocaleDateString()} - ${period_end.toLocaleDateString()}`
          const year: number = reportDate.getFullYear()
          const month: number = reportDate.getMonth()
          const lastDay: Date = new Date(
            year,
            month,
            new Date(year, month, 0).getDate()
          )
          const isWithinRange: boolean =
            reportDate >= period_start && lastDay <= period_end
          const bgcolor = isWithinRange ? success : error

          return (
            <ListItem key={id}>
              <Chip
                avatar={
                  <Avatar>
                    <MemoryTwoTone />
                  </Avatar>
                }
                label={name}
              />

              <Stack spacing={1} sx={{ ml: 1 }}>
                <Chip
                  avatar={
                    <Avatar>
                      {isWithinRange ? <CheckTwoTone /> : <CloseTwoTone />}
                    </Avatar>
                  }
                  label={label}
                  key={label}
                  sx={{ bgcolor, mr: 1 }}
                />
              </Stack>
            </ListItem>
          )
        })}
      </Collapse>
    </Alert>
  )
}
